import { LoadingButton } from "@mui/lab";
import {
  Box,
  FormControl,
  FormHelperText,
  Stack,
  Typography
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import {
  settingsInputMinHeightPx,
  settingsSelectSx
} from "~/features/settings/settings.styles";
import { LanguageCode } from "~/localization_i18n";
import { setLanguage } from "~/redux/actions/site";

import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectAuth0Profile } from "~/redux/selectors/authSelectors";

export const LanguageSettings = () => {
  const siteLanguageCode = useAppSelector((state) => state.site.languageCode);
  const { site_defaultLanguageCode } = useAppSelector(selectServerConfig);
  const profile = useAppSelector(selectAuth0Profile);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const insufficientPermissionsLabel = t(
    "language settings insufficient permissions"
  );

  const [pendingLanguageCode, setPendingLanguageCode] =
    useState<LanguageCode>(siteLanguageCode);

  useEffect(() => {
    setPendingLanguageCode(siteLanguageCode);
  }, [siteLanguageCode]);

  const languageCodeMap: Partial<Record<LanguageCode, string>> = useMemo(
    () => ({
      ...(site_defaultLanguageCode === "bs"
        ? { bs: "English" }
        : { en: "English" }),
      ko: "Korean",
      es: "Spanish",
      fr: "French",
      ja: "Japanese"
    }),
    [site_defaultLanguageCode]
  );

  const siteLanguageCodes = useMemo(
    () => Object.keys(languageCodeMap) as LanguageCode[],
    [languageCodeMap]
  );

  const handleLanguageSelectChange = useCallback(
    (event: SelectChangeEvent) => {
      const label = event.target.value;
      const match = siteLanguageCodes.find(
        (code) => languageCodeMap[code] === label
      );
      if (match) {
        setPendingLanguageCode(match);
      }
    },
    [languageCodeMap, siteLanguageCodes]
  );

  const handleAssignLanguage = useCallback(() => {
    dispatch(setLanguage(pendingLanguageCode));
  }, [dispatch, pendingLanguageCode]);

  const languageSettingsPermissionWarning = useMemo(() => {
    if (!profile) {
      return "";
    }
    const requiredRoles = [
      "powerpick-picker",
      "powerpick-inventory-manager",
      "powerpick-receiver",
      "client-administrator",
      "qubit-workstation-configurer"
    ];
    return profile.roles.some((role) => requiredRoles.includes(role))
      ? ""
      : insufficientPermissionsLabel;
  }, [profile, insufficientPermissionsLabel]);

  const selectedLabel = languageCodeMap[pendingLanguageCode] ?? "";
  const isLanguageDirty = pendingLanguageCode !== siteLanguageCode;

  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      {languageSettingsPermissionWarning ? (
        <Typography variant="body2" color="error">
          {languageSettingsPermissionWarning}
        </Typography>
      ) : null}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { sm: "center" },
          gap: { xs: 1.5, sm: 2 },
          flexWrap: "wrap"
        }}
      >
        <FormControl sx={{ maxWidth: 400, minWidth: 0 }} size="small" fullWidth>
          <Select
            id="user-language-preview-select"
            variant="outlined"
            value={selectedLabel}
            onChange={handleLanguageSelectChange}
            displayEmpty
            aria-label={t("language")}
            aria-describedby="user-language-preview-helper"
            sx={settingsSelectSx}
          >
            {siteLanguageCodes.map((langCode) => (
              <MenuItem key={langCode} value={languageCodeMap[langCode]}>
                {languageCodeMap[langCode]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <LoadingButton
          variant="contained"
          color="secondary"
          disabled={!isLanguageDirty}
          onClick={handleAssignLanguage}
          aria-label="assign-language"
          sx={{
            minHeight: settingsInputMinHeightPx,
            flexShrink: 0,
            alignSelf: { xs: "flex-end", sm: "center" }
          }}
        >
          {t("assign")}
        </LoadingButton>
      </Box>
      <FormHelperText id="user-language-preview-helper" sx={{ m: 0 }}>
        {t("language selection helper")}
      </FormHelperText>
    </Stack>
  );
};
